import classNames from "classnames";
import { useState } from "react";

export type ToggleButtonValue = string | boolean;

export type ToggleButtonProps<Value extends ToggleButtonValue> = {
  value?: Value;
  options: {
    value: Value;
    label: string;
  }[];
  onChange?: (value: Value) => void;
  hasError?: boolean;
  errorTranslation: string;
};

export const ToggleButton = <Value extends ToggleButtonValue>({
  value,
  options,
  onChange,
  hasError,
  errorTranslation,
}: ToggleButtonProps<Value>) => {
  const [uncontrolledValue, setUncontrolledValue] = useState<Value | undefined>(
    undefined,
  );

  const usedValue = value !== undefined ? value : uncontrolledValue;

  const handleClick = (option: Value) => {
    if (onChange) {
      onChange(option);
    } else {
      setUncontrolledValue(option);
    }
  };

  return (
    <div className="flex flex-col sm:items-center gap-1 min-w-max">
      <div className="flex justify-start w-full">
        <div
          className={classNames(
            "grid w-full max-w-sm sm:max-w-none border rounded-lg overflow-hidden",
            {
              "border-primary": !hasError,
              "border-error": hasError,
            },
          )}
          style={{ gridTemplateColumns: `repeat(${options.length}, 1fr)` }}
        >
          {options.map((option, index) => (
            <button
              key={index}
              type="button"
              className={`px-4 py-2 text-sm font-medium transition-all duration-200 ease-in-out ${
                usedValue === option.value
                  ? "bg-primary text-primary-contrast"
                  : "bg-white text-text-primary"
              } ${
                index < options.length - 1 ? "border-r border-gray-300" : ""
              }`}
              onClick={() => handleClick(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      {hasError && (
        <span className="text-sm text-error">{errorTranslation}</span>
      )}
    </div>
  );
};
